/**
 * Pure geometry for draggable side panels. No React, no DOM — this module
 * is where every decision a resize makes is testable, because the hook and
 * the handle that use it cannot be unit-tested in this repo (`bun test`
 * runs without a DOM).
 */

/**
 * Which LOGICAL inline edge of the panel the handle sits on. Logical, not
 * physical, so it stays constant across LTR and RTL: the nav sidebar's
 * handle is always on its inline-`end` edge, every other panel's is always
 * on its inline-`start` edge.
 */
export type PanelEdge = 'start' | 'end';

export type PanelBounds = { min: number; max: number };

/**
 * Every resizable panel in the app. A closed, app-owned set of layout
 * surfaces — not a user-extensible vocabulary — so a union is right here.
 */
export type PanelId =
  | 'nav-sidebar'
  | 'info-sidebar'
  | 'ocr-extraction'
  | 'scan-thumb-rail'
  | 'scan-side-panel'
  | 'qr-review-panel'
  | 'ocr-page-assign'
  | 'ocr-page-results';

/** The root-scoped custom property a panel's width is painted onto. */
export function panelCssVar(panelId: PanelId): string {
  return `--panel-${panelId}-width`;
}

/**
 * The value a consumer applies. The fallback is what renders before the
 * store rehydrates, which is where the nav sidebar's cookie plugs in.
 *
 * Lives here rather than in the hook because `dashboard/layout.tsx` is a
 * SERVER component: it cannot import a value out of a `'use client'`
 * module and call it.
 */
export function panelWidthValue(panelId: PanelId, fallbackPx: number): string {
  return `var(${panelCssVar(panelId)}, ${fallbackPx}px)`;
}

/**
 * Shell sidebar sizing.
 *
 * These live here, and NOT beside the components that use them, because
 * `dashboard/layout.tsx` is a server component. Next's flight loader
 * rewrites every export of a `'use client'` module to a client reference in
 * the server graph, so importing them from `app-sidebar.tsx` would hand the
 * layout a function instead of a number — silently, with no error — and the
 * width would serialize as `NaNpx`. This module has no `'use client'`, so
 * both graphs get the real values.
 */
export const NAV_SIDEBAR_DEFAULT_WIDTH = 256;
export const NAV_SIDEBAR_BOUNDS: PanelBounds = { min: 200, max: 480 };
export const INFO_SIDEBAR_DEFAULT_WIDTH = 352;
export const INFO_SIDEBAR_BOUNDS: PanelBounds = { min: 240, max: 560 };

/** One arrow-key press. */
export const KEYBOARD_STEP_PX = 16;

/** Width of a collapsed panel's toggle strip — the old `2.5rem`. */
export const COLLAPSED_STRIP_PX = 40;

/** Width of a resize handle when it occupies a grid column of its own. */
export const RESIZE_HANDLE_PX = 4;

export function clampWidth(width: number, { min, max }: PanelBounds): number {
  if (Number.isNaN(width)) return min;
  return Math.min(Math.max(width, min), max);
}

/**
 * Which way the pointer has to move for this panel to get wider, as a
 * multiplier on the raw `Δx`.
 *
 * The only place direction is handled. Moving "toward the inline end" is
 * `+Δx` in LTR and `−Δx` in RTL; a panel whose handle is on its inline-end
 * edge grows that way, and one whose handle is on its inline-start edge
 * grows the opposite way.
 */
export function growthSign(edge: PanelEdge, dir: 'ltr' | 'rtl'): 1 | -1 {
  const towardEnd = dir === 'rtl' ? -1 : 1;
  return (edge === 'end' ? towardEnd : -towardEnd) as 1 | -1;
}

export function nextWidthFromDrag({
  startWidth,
  startX,
  currentX,
  edge,
  dir,
  bounds
}: {
  startWidth: number;
  startX: number;
  currentX: number;
  edge: PanelEdge;
  dir: 'ltr' | 'rtl';
  bounds: PanelBounds;
}): number {
  const delta = (currentX - startX) * growthSign(edge, dir);
  return clampWidth(startWidth + delta, bounds);
}

/**
 * Returns the next width, or `null` when the key is not one this handle
 * owns — the caller uses `null` to decide whether to call `preventDefault`,
 * so an unhandled key has to stay distinguishable from "no change".
 */
export function nextWidthFromKey({
  width,
  key,
  edge,
  dir,
  bounds,
  step = KEYBOARD_STEP_PX,
  defaultWidth
}: {
  width: number;
  key: string;
  edge: PanelEdge;
  dir: 'ltr' | 'rtl';
  bounds: PanelBounds;
  step?: number;
  defaultWidth: number;
}): number | null {
  if (key === 'Home') return clampWidth(defaultWidth, bounds);
  if (key !== 'ArrowLeft' && key !== 'ArrowRight') return null;

  // Model the key as a pointer nudge, so the keyboard and the drag can
  // never disagree about which way is wider.
  const pointerDelta = key === 'ArrowRight' ? step : -step;
  return clampWidth(width + pointerDelta * growthSign(edge, dir), bounds);
}

/**
 * `grid-template-columns` for the photo-scanning body.
 *
 * Replaces six literal `grid-cols-[…]` class strings, which existed only
 * because Tailwind's JIT needs class names written out; an inline style has
 * no such constraint.
 *
 * `railWidth`/`panelWidth` are CSS length EXPRESSIONS, not numbers — the
 * caller passes `var(--panel-…-width, 216px)`, so a drag changes the
 * variable while this string stays byte-identical and the component never
 * re-renders mid-drag.
 *
 * Column order: content | railHandle | rail | panelHandle | panel.
 * A collapsed panel keeps its columns but shrinks the handle to `0px` and
 * the panel to the toggle strip, so the caller's children stay aligned
 * with the columns.
 */
export function buildScanGridTemplate({
  railWidth,
  panelWidth,
  showThumbnails,
  showSidePanel,
  viewMode
}: {
  railWidth: string;
  panelWidth: string;
  showThumbnails: boolean;
  showSidePanel: boolean;
  viewMode: 'document' | 'grid';
}): string {
  const columns: string[] = ['1fr'];

  // Grid mode drops the rail entirely rather than collapsing it: QrPageGrid
  // already shows every page, so a strip would toggle nothing useful.
  if (viewMode === 'document') {
    columns.push(showThumbnails ? `${RESIZE_HANDLE_PX}px` : '0px');
    columns.push(showThumbnails ? railWidth : `${COLLAPSED_STRIP_PX}px`);
  }

  columns.push(showSidePanel ? `${RESIZE_HANDLE_PX}px` : '0px');
  columns.push(showSidePanel ? panelWidth : `${COLLAPSED_STRIP_PX}px`);

  return columns.join(' ');
}
